/* Example usage:
<marquee-component speed="100">
    <div class="marquee">
        <div class="marquee-content">
            <div class="marquee-item">Item 1</div>
            <div class="marquee-item">Item 2</div>
            <div class="marquee-item">Item 3</div>
            <div class="marquee-item">Item 4</div>
        </div>
    </div>
</marquee-component>
*/

if (!customElements.get('marquee-component')) {
  const DEBUG = false;
  const ANIMATION_DISTANCE_PCT = 50;
  const ANIMATION_PCT_FACTOR = 100 / ANIMATION_DISTANCE_PCT;

class MarqueeComponent extends HTMLElement {
  constructor() {
    super();
    this.hash = window.crypto.randomUUID().slice(0, 7);
    this.lastWidth = 0;
    this.observerActive = true;

    if (DEBUG) {
      this.addEventListener('animationiteration', (e) => {
        console.log(`animationiteration e`, e);
      });
    }
  }

  connectedCallback() {
    setTimeout(() => {
      this.initialise();

      this.resizeObserver = new ResizeObserver((entries) => {
        // Skip if observer is temporarily disabled (e.g., during modal open/close)
        if (!this.observerActive) return;

        for (let entry of entries) {
          const newWidth = entry.contentRect.width;
          // Only re-initialize if width actually changed significantly (more than 10px)
          if (Math.abs(newWidth - this.lastWidth) > 10) {
            this.lastWidth = newWidth;
            this.initialise();
          }
        }
      });
      this.resizeObserver.observe(this);

      // Listen for modal state changes to pause/resume observer
      this.setupModalListeners();
    }, 250);
  }

  setupModalListeners() {
    // Watch for body overflow changes (indicates modal open/close)
    const bodyObserver = new MutationObserver(() => {
      const modalActive = document.body.style.overflow === 'hidden';
      this.observerActive = !modalActive;
    });

    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style']
    });

    this.bodyObserver = bodyObserver;
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.bodyObserver) {
      this.bodyObserver.disconnect();
    }
  }

  initialise() {
    const marquee = this.querySelector('.marquee');
    const marqueeContent = this.querySelector('.marquee-content');
    const windowWidth = window.innerWidth;

    this.removeClonedContainers();
    this.addInitialStyles();

    const marqueeContentWidth = marqueeContent.scrollWidth;
    const numContainers = ANIMATION_PCT_FACTOR * Math.ceil(windowWidth / marqueeContentWidth);

    for (let i = 0; i < numContainers - 1; i++) {
      const clone = marqueeContent.cloneNode(true);
      marquee.appendChild(clone);
    }

    this.setAnimationDuration();
  }

  removeClonedContainers() {
    const marqueeContentEls = this.querySelectorAll('.marquee-content');
    marqueeContentEls?.forEach((el, index) => {
      if (index > 0) el.remove();
    });
  }

  setAnimationDuration() {
    if (!this.isConnected) return;
    const marquee = this.querySelector('.marquee');

    let speed = 0;
    try {
      speed = Number(this.getAttribute('speed')) ?? 0;
    } catch (e) {
      console.error('Error parsing speed attribute', e);
      speed = 0;
    }

    if (speed <= 0) {
      marquee.style['animation-play-state'] = 'paused';
      return;
    } else {
      marquee.style['animation-play-state'] = 'running';
    }

    const marqueeWidth = marquee.scrollWidth;
    const animationDistance = marqueeWidth / ANIMATION_PCT_FACTOR;
    const animationDuration = animationDistance / speed;

    if (DEBUG) {
      console.log({ marqueeWidth, animationDuration, animationDistance });
    }

    this.appendAnimationStyles(animationDuration, animationDistance);
  }

  static get observedAttributes() {
    return ['speed'];
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'speed' && oldVal !== newVal) {
      this.addInitialStyles();
      this.setAnimationDuration();
    }
  }

  addInitialStyles() {
    const styleTags = this.querySelectorAll('style');
    if (styleTags) {
      styleTags.forEach((style) => style.remove());
    }
    const marqueeEl = this.querySelector('.marquee');
    marqueeEl.classList.add(`marquee-${this.hash}`);

    const style = document.createElement('style');
    style.textContent = /* css */ `
      marquee-component {
        display: block;
        width: 100%;
        overflow: hidden;
      }

      .marquee-${this.hash} {
        display: flex;
        width: max-content;
      }

      .marquee-${this.hash} .marquee-content {
        display: flex;
        width: max-content;
      }
    `;
    this.prepend(style);
  }

  appendAnimationStyles(animationDuration, animationDistance) {
    const style = this.querySelector('style');
    const animationStyles = /* css */ `
      .marquee-${this.hash} {
        animation: marquee-${this.hash}-animation ${animationDuration}s linear infinite;
      }

      @keyframes marquee-${this.hash}-animation {
        0% {
          transform: translateX(0);
        }
        100% {
          transform: translateX(-${animationDistance}px);
        }
      }
    `;
    style.textContent += animationStyles;
  }
}

  customElements.define('marquee-component', MarqueeComponent);
}
